import { useSyncExternalStore } from "react";

/** Salt's scale, held fixed so a layout shift can be attributed to the engine, not the scale. */
export const BREAKPOINTS = { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 } as const;
export type Breakpoint = keyof typeof BREAKPOINTS;

const ASCENDING = (Object.keys(BREAKPOINTS) as Breakpoint[]).sort(
  (a, b) => BREAKPOINTS[a] - BREAKPOINTS[b],
);

/**
 * matchMedia, not `window.innerWidth`. `src/test/responsive-viewport.tsx` simulates a viewport
 * by answering `(min-width: Npx)` queries and never touches `innerWidth`, so reading innerWidth
 * ignores the harness and reports a desktop at every width.
 */
function matched(): Breakpoint[] {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return [];
  return ASCENDING.filter((n) => window.matchMedia(`(min-width: ${BREAKPOINTS[n]}px)`).matches);
}

/** A string, because useSyncExternalStore compares with Object.is and a fresh array loops. */
function snapshot(): string {
  return matched().join(",");
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const lists = ASCENDING.map((n) => window.matchMedia(`(min-width: ${BREAKPOINTS[n]}px)`));
  for (const l of lists) l.addEventListener?.("change", onChange);
  return () => {
    for (const l of lists) l.removeEventListener?.("change", onChange);
  };
}

/** Empty is legal: setup.ts stubs matchMedia false, and the app relies on inheritance then. */
export function useMatchedBreakpoints(): Breakpoint[] {
  const joined = useSyncExternalStore(subscribe, snapshot, () => "");
  return joined === "" ? [] : (joined.split(",") as Breakpoint[]);
}

export type Responsive<T> = T | Partial<Record<Breakpoint, T>>;

function isResponsiveObject<T>(v: Responsive<T>): v is Partial<Record<Breakpoint, T>> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  return Object.keys(v).every((k) => k in BREAKPOINTS);
}

export function resolveResponsive<T>(v: Responsive<T>, m: Breakpoint[]): T | undefined {
  if (v === undefined || v === null) return v as undefined;
  if (!isResponsiveObject(v)) return v as T;
  for (let i = m.length - 1; i >= 0; i -= 1) {
    const c = v[m[i]];
    if (c !== undefined) return c;
  }
  return undefined;
}
